<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm transition-opacity duration-300"
  >
    <div
      class="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden mx-4 border border-gray-100 transform transition-all duration-300 scale-95 hover:scale-100"
    >
      <!-- Header -->
      <div
        class="px-6 py-4 flex justify-between items-center bg-gradient-to-r from-teal-500 to-emerald-500"
      >
        <h2 class="text-xl font-semibold text-white tracking-tight">
          {{ title }}
        </h2>
        <button
          @click="$emit('close')"
          class="p-1 rounded-full hover:bg-white/20 transition-colors duration-200"
          aria-label="Close modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Form Content -->
      <div class="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
        <form @submit.prevent="handleSubmit" class="space-y-5">
          <!-- Category & Subcategory -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div class="space-y-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t("products.addModal.category") }}
                <span class="text-red-500">*</span></label
              >
              <select
                v-model="form.category_id"
                @change="onCategoryChange"
                required
                class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              >
                <option disabled value="">
                  {{ $t("products.addModal.selectCategory") }}
                </option>
                <option
                  v-for="cat in categories"
                  :key="cat.id"
                  :value="cat.id"
                  class="py-1"
                >
                  {{ cat.name }}
                </option>
              </select>
            </div>

            <div v-if="subcategories.length > 0" class="space-y-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t("products.addModal.subcategory") }}
                <span class="text-red-500">*</span></label
              >
              <select
                v-model="form.sub_category_id"
                @change="onSubcategoryChange"
                required
                class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              >
                <option disabled value="">
                  {{ $t("products.addModal.selectSubcategory") }}
                </option>
                <option
                  v-for="sub in subcategories"
                  :key="sub.id"
                  :value="sub.id"
                  class="py-1"
                >
                  {{ sub.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-1 gap-5">
              <!-- Country -->
              <div class="space-y-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t("products.addModal.country") }}
                  <span class="text-red-500">*</span>
                </label>
                <select
                  v-model="form.country_id"
                  @change="onCountryChange"
                  required
                  :disabled="loadingCountries"
                  class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
                >
                  <option disabled value="">
                    {{ loadingCountries ? 'Loading countries...' : $t("products.addModal.selectCountry") }}
                  </option>
                  <option
                    v-for="country in countries"
                    :key="country.id"
                    :value="country.id"
                  >
                    {{ country.name }}
                  </option>
                </select>
              </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- State -->
            <div class="space-y-1" v-if="form.country_id">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                {{ $t("products.addModal.state") }}
                <span class="text-red-500">*</span></label
              >
              <select
                v-model="form.state_id"
                @change="onStateChange"
                required
                :disabled="loadingStates"
                class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              >
                <option disabled value="">
                  {{ loadingStates ? 'Loading...' : $t("products.addModal.selectState") }}
                </option>
                <option
                  v-for="state in states"
                  :key="state.id"
                  :value="state.id"
                >
                  {{ state.name }}
                </option>
              </select>
            </div>

            <!-- Area -->
            <div v-if="areas.length > 0" class="space-y-1">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >{{ $t("products.addModal.area") }}
                <span class="text-red-500">*</span></label
              >
              <select
                v-model="form.area_id"
                required
                :disabled="loadingAreas"
                class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              >
                <option disabled value="">
                  {{ loadingAreas ? 'Loading...' : $t("products.addModal.selectArea") }}
                </option>
                <option v-for="area in areas" :key="area.id" :value="area.id">
                  {{ area.name }}
                </option>
              </select>
            </div>
          </div>



          <!-- Static Fields -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div
              v-for="(field, index) in fields"
              :key="index"
              :class="[
                'space-y-1',
                field.type === 'textarea' ? 'md:col-span-2' : '',
              ]"
            >
              <label class="block text-sm font-medium text-gray-700">
                {{ field.label }}
                <span v-if="field.required" class="text-red-500">*</span>
              </label>
              <!-- SELECT -->
              <!-- SELECT -->
              <select
                v-if="field.type === 'select'"
                v-model="form[field.model]"
                :required="field.required || false"
                class="w-full h-12 px-3 text-base border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              >
                <option disabled value="">
                  {{ $t("Select") }} {{ field.label }}
                </option>
                <option
                  v-for="opt in field.options"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>

              <!-- TEXTAREA -->
              <textarea
                v-else-if="field.type === 'textarea'"
                v-model="form[field.model]"
                :placeholder="field.placeholder || ''"
                :required="field.required || false"
                rows="4"
                class="w-full px-3 py-2.5 text-base border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
              />

              <!-- TEXT / NUMBER / PRICE ICON SUPPORT -->
              <div v-else class="relative">
                <input
                  v-model="form[field.model]"
                  :type="field.model === 'price' ? 'text' : field.type"
                  inputmode="decimal"
                  :placeholder="field.placeholder || ''"
                  :required="field.required || false"
                  @focus="clearPriceOnFocus(field)"
                  @blur="resetPriceIfEmpty(field)"
                  @input="filterPriceInput(field)"
                  class="w-full h-12 px-10 text-base border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
                />
                <span
                  v-if="field.priceIcon"
                  class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 text-base"
                >
                  <img src="/R.svg" alt="SAR" class="w-4 h-4 opacity-80" />
                </span>
              </div>
            </div>
          </div>

          <!-- Dynamic Fields -->
          <div v-if="dynamicFields.length > 0" class="space-y-5 pt-2">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div v-for="df in dynamicFields" :key="df.id" class="space-y-1">
                <label class="block text-sm font-medium text-gray-700">
                  {{ df.label }}
                </label>

                <!-- SELECT -->
                <select
                  v-if="df.type === 'select'"
                  v-model="form.fields[df.id]"
                  class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
                >
                  <option disabled value="">
                    {{ $t("Select") }} {{ df.label }}
                  </option>
                  <option
                    v-for="(opt, idx) in df.options"
                    :key="idx"
                    :value="opt"
                  >
                    {{ opt }}
                  </option>
                </select>

                <!-- RADIO -->
                <div v-else-if="df.type === 'radio'" class="space-y-1">
                  <div
                    v-for="(opt, idx) in df.options"
                    :key="idx"
                    class="flex items-center gap-2"
                  >
                    <input
                      type="radio"
                      :name="'radio_' + df.id"
                      :value="opt"
                      v-model="form.fields[df.id]"
                      class="text-teal-500 focus:ring-teal-400"
                    />
                    <label class="text-sm text-gray-700">{{ opt }}</label>
                  </div>
                </div>
                <!-- COLOR (Dropdown with predefined Arabic values) -->
                <select
                  v-else-if="df.type === 'color'"
                  v-model="form.fields[df.id]"
                  class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
                >
                  <option disabled value="">
                    {{ $t("products.addModal.selectColor") }}
                  </option>
                  <option
                    v-for="(color, i) in colorOptions"
                    :key="i"
                    :value="color"
                  >
                    {{ color }}
                  </option>
                </select>

                <!-- CHECKBOX -->
                <div v-else-if="df.type === 'checkbox'" class="space-y-1">
                  <div
                    v-for="(opt, idx) in df.options"
                    :key="idx"
                    class="flex items-center gap-2"
                  >
                    <input
                      type="checkbox"
                      :value="opt"
                      :id="`checkbox_${df.id}_${idx}`"
                      v-model="form.fields[df.id]"
                      class="text-teal-500 focus:ring-teal-400"
                    />
                    <label
                      :for="`checkbox_${df.id}_${idx}`"
                      class="text-sm text-gray-700"
                      >{{ opt }}</label
                    >
                  </div>
                </div>

                <!-- TEXT / NUMBER -->
                <input
                  v-else
                  v-model="form.fields[df.id]"
                  :type="df.type === 'number' ? 'number' : 'text'"
                  class="w-full px-3 py-2.5 border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400 transition-all"
                />
              </div>
            </div>
          </div>

          <!-- wrap the whole file section so we can scroll to it reliably -->
          <div class="space-y-1" ref="fileSection">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t("fileInput.label") }} <span class="text-red-500">*</span>
            </label>

            <div class="flex items-center gap-4 rtl:gap-4">
              <!-- Hidden input: remove `required` to disable native bubble -->
              <input
                ref="fileInput"
                type="file"
                multiple
                accept="image/*"
                @change="handleImages"
                class="sr-only"
                :aria-invalid="hasImageError ? 'true' : 'false'"
                aria-describedby="fileError"
              />

              <!-- Custom trigger -->
              <button
                type="button"
                @click="$refs.fileInput.click()"
                :class="[
                  'flex items-center gap-2 rtl:gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors',
                  hasImageError
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-teal-50 text-teal-700 hover:bg-teal-100',
                ]"
              >
                {{ $t("fileInput.chooseFiles") }}
                <!-- icon ... -->
              </button>

              <span
                :class="[
                  'text-sm',
                  hasImageError ? 'text-red-500' : 'text-gray-500',
                ]"
              >
                {{ fileName || $t("fileInput.noFileChosen") }}
              </span>
            </div>

            <!-- Custom validation message (same as you had) -->
          </div>

          <!-- Location Section -->
          <div class="space-y-3">
            <label class="block text-sm font-medium text-gray-700">
              {{ $t("products.addModal.location") }}
            </label>

            <div class="flex flex-col sm:flex-row gap-3">
              <div class="flex-1">
                <label class="block text-xs text-gray-500 mb-1">{{
                  $t("products.addModal.searchLocation")
                }}</label>
                <div
                  class="flex shadow-sm overflow-hidden rounded-lg"
                  :class="
                    $i18n.locale === 'ar' ? 'flex-row-reverse' : 'flex-row'
                  "
                >
                  <!-- Search Input -->
                  <input
                    v-model="searchQuery"
                    @keyup.enter="searchLocation"
                    type="text"
                    :placeholder="$t('products.addModal.searchLocation')"
                    class="flex-1 px-3 py-2.5 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-300 focus:border-teal-400"
                    :style="
                      $i18n.locale === 'ar'
                        ? 'border-left: none; border-top-left-radius: 0; border-bottom-left-radius: 0;'
                        : 'border-right: none; border-top-right-radius: 0; border-bottom-right-radius: 0;'
                    "
                  />

                  <!-- Search Button -->
                  <button
                    type="button"
                    @click="searchLocation"
                    class="px-4 bg-teal-500 text-white hover:bg-teal-600 transition-colors flex items-center justify-center"
                    :style="
                      $i18n.locale === 'ar'
                        ? 'border-top-right-radius: 0; border-bottom-right-radius: 0;'
                        : 'border-top-left-radius: 0; border-bottom-left-radius: 0;'
                    "
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="">
                <label
                  class="block text-xs text-gray-500 mb-1"
                  style="visibility: hidden"
                  >{{ $t("products.addModal.searchLocation") }}</label
                >
                <button
                  type="button"
                  @click="useMyLocation"
                  class="px-4 py-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-teal-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span class="text-sm">
                    {{ $t("products.addModal.myLocation") }}</span
                  >
                </button>
              </div>
            </div>

            <div
              ref="mapContainer"
              class="h-64 w-full rounded-lg border border-gray-200 shadow-sm overflow-hidden"
            ></div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs text-gray-500 mb-1">
                  {{ $t("products.addModal.latitude") }}</label
                >
                <div
                  class="flex items-center px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                  <span class="text-sm text-gray-700">{{
                    form.latitude || $t("products.addModal.noSet")
                  }}</span>
                </div>
              </div>
              <div>
                <label class="block text-xs text-gray-500 mb-1">
                  {{ $t("products.addModal.longitude") }}</label
                >
                <div
                  class="flex items-center px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                    />
                  </svg>
                  <span class="text-sm text-gray-700">{{
                    form.longitude || $t("products.addModal.noSet")
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="pt-2">
            <button
              type="submit"
              class="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all transform hover:scale-[1.01]"
            >
              {{ $t("products.addModal.submit") }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import heic2any from "heic2any";
// Fix Leaflet's default icon issue
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: iconUrl,
  shadowUrl: iconShadow,
});

export default {
  name: "DynamicAddModal",
  props: {
    title: { type: String, default: "Add Item" },
    fields: { type: Array, default: () => [] },
    initialForm: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      // ... existing data
      hasImageError: false,
      showImageError: false,

      form: {
        ...this.initialForm,
        country_id: "",
        state_id: "",
        area_id: "",
        category_id: "",
        sub_category_id: "",
        currency_id: "", // Will be set to default after currencies load
        fields: {},
        images: [],
      },
  countries: [],
  states: [],
  loadingStates: false,
  loadingAreas: false,
  loadingCountries: false,
      fileName: "",
      areas: [],
      categories: [],
      subcategories: [],
      dynamicFields: [],
      map: null,
      marker: null,
      searchQuery: "",
      colorOptionsEn: [
        "White",
        "Red",
        "Green",
        "Blue",
        "Light Blue",
        "Cement",
        "Black",
        "Yellow",
        "Petrol",
        "Orange",
        "Bronze",
        "Purple",
        "Brown",
        "Beige",
        "Tan",
        "Turquoise",
        "Maroon",
        "Gold",
        "Grey",
        "Pink",
        "Olive",
        "Silver",
        "Navy",
      ],
      // Arabic colours
      colorOptionsAr: [
        "أبيض",
        "أحمر",
        "أخضر",
        "أزرق",
        "أزرق فاتح",
        "أسمنتي",
        "أسود",
        "أصفر",
        "بترولي",
        "برتقالي",
        "برونزي",
        "بنفسجي",
        "بني",
        "بيج",
        "تان",
        "تركواز",
        "خمري",
        "ذهبي",
        "رمادي",
        "زهري",
        "زيتي",
        "فضي",
        "كحلي",
      ],
      colorHexCodes: {
        White: "#FFFFFF",
        Red: "#FF0000",
        Green: "#008000",
        Blue: "#0000FF",
        "Light Blue": "#ADD8E6",
        Cement: "#B6B6B4", // Light greyish tone (cement-like)
        Black: "#000000",
        Yellow: "#FFFF00",
        Petrol: "#005F6B", // Dark teal (common petrol color)
        Orange: "#FFA500",
        Bronze: "#CD7F32",
        Purple: "#800080",
        Brown: "#A52A2A",
        Beige: "#F5F5DC",
        Tan: "#D2B48C",
        Turquoise: "#40E0D0",
        Maroon: "#800000",
        Gold: "#FFD700",
        Grey: "#808080",
        Pink: "#FFC0CB",
        Olive: "#808000",
        Silver: "#C0C0C0",
        Navy: "#000080",
      },
    };
  },
  computed: {
    colorOptions() {
      return this.$i18n.locale === "ar"
        ? this.colorOptionsAr
        : this.colorOptionsEn;
    },
  },
  mounted() {
    this.initMap();
  },
  created() {
    console.log("DynamicAddModal created - fetching data...");
    this.fetchCategories();
    this.fetchCountries();
  },
  watch: {
    dynamicFields: {
      handler(fields) {
        fields.forEach((f) => {
          if (!Object.prototype.hasOwnProperty.call(this.form.fields, f.id)) {
            if (f.type === "checkbox") {
              this.form.fields[f.id] = []; // ✅ مصفوفة للـ checkbox
            } else {
              this.form.fields[f.id] = "";
            }
          }
        });
      },
      immediate: true,
    },
  },
  methods: {
    filterPriceInput(field) {
      if (field.model === "price") {
        this.form.price = this.form.price.toString().replace(/[^\d.]/g, "");
      }
    },
    clearPriceOnFocus(field) {
      if (
        field.model === "price" &&
        (this.form.price === 0 || this.form.price === "0")
      ) {
        this.form.price = "";
      }
    },
    resetPriceIfEmpty(field) {
      if (
        field.model === "price" &&
        (this.form.price === "" || this.form.price === null)
      ) {
        this.form.price = 0;
      }
    },
    async fetchCountries() {
      try {
        this.loadingCountries = true;
        const lang = localStorage.getItem("lang_code") || "ar";
        console.log("Fetching countries with lang:", lang);
        const res = await api.get("/v1/countries", { params: { lang } });
        this.countries = res.data;
        console.log("Countries loaded:", this.countries);
        
        // If no countries loaded, add a fallback for testing
        if (!this.countries || this.countries.length === 0) {
          console.warn("No countries found, using fallback");
          this.countries = [
            { id: 1, name: "Saudi Arabia" },
            { id: 2, name: "UAE" },
            { id: 3, name: "Kuwait" }
          ];
        }
      } catch (err) {
        console.error("Failed to load countries", err);
        // Add fallback countries in case of API error
        this.countries = [
          { id: 1, name: "Saudi Arabia" },
          { id: 2, name: "UAE" },
          { id: 3, name: "Kuwait" }
        ];
      } finally {
        this.loadingCountries = false;
      }
    },
    async fetchCurrencies() {
      // Currency is always SAR - no need to fetch from API
      console.log("Currency is hardcoded to SAR - skipping API call");
      this.currencies = [];
      this.form.currency_id = null; // Will always use SAR currency code
    },
    async onCountryChange() {
      this.states = [];
      this.form.state_id = "";
      this.areas = [];
      this.form.area_id = "";
      if (this.form.country_id) {
        await this.fetchStates(this.form.country_id);
      }
    },
    async fetchStates(countryId) {
      try {
        this.loadingStates = true;
        const lang = localStorage.getItem("lang_code") || "ar";
        const res = await api.get("/v1/states", { params: { country_id: countryId, lang } });
        this.states = res.data;
      } catch (err) {
        console.error("Failed to load states", err);
      } finally {
        this.loadingStates = false;
      }
    },
    async onStateChange() {
      try {
        this.loadingAreas = true;
        this.areas = [];
        this.form.area_id = "";
        const res = await api.get(`/v1/states/${this.form.state_id}/areas`);
        this.areas = res.data;
      } catch (err) {
        console.error("Failed to load areas", err);
      } finally {
        this.loadingAreas = false;
      }
    },
    async handleImages(event) {
      const list = Array.from(event.target.files || []);

      if (!list.length) {
        this.form.images = [];
        this.fileName = "";
        this.hasImageError = true;
        this.showImageError = true;

        // important: clear native input so it doesn't report stale files
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";
        return;
      }

      const files = event.target.files;
      this.fileName = files.length
        ? `${files.length} ${this.$t("form.filesSelected")}`
        : "";

      // Reset image validation error when files are selected
      if (files.length > 0) {
        this.hasImageError = false;
        this.showImageError = false;
      }

      const convertedImages = [];

      for (const file of files) {
        const isHeicExt = file.name.toLowerCase().endsWith(".heic");
        const isImageTypeHeif =
          file.type === "image/heic" || file.type === "image/heif";

        console.log("Processing:", file.name, file.type);

        if (isHeicExt && isImageTypeHeif) {
          try {
            const convertedBlob = await heic2any({
              blob: file,
              toType: "image/jpeg",
              quality: 0.9,
            });

            const newFile = new File(
              [convertedBlob],
              file.name.replace(/\.heic$/i, ".jpg"),
              { type: "image/jpeg" }
            );

            convertedImages.push(newFile);
            console.log("Converted:", newFile.name);
          } catch (err) {
            console.error("HEIC conversion failed", err);
            convertedImages.push(file);
          }
        } else {
          convertedImages.push(file);
        }
      }

      this.form.images = convertedImages;
      console.log("Final images list:", this.form.images);
    },

    validateImages() {
      if (!this.form.images || this.form.images.length === 0) {
        this.hasImageError = true;
        this.showImageError = true;
        return false;
      }
      this.hasImageError = false;
      this.showImageError = false;
      return true;
    },
    async fetchCategories() {
      try {
        const res = await api.get("/v1/categories");
        this.categories = res.data;
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    },
    async onCategoryChange() {
      try {
        this.subcategories = [];
        this.dynamicFields = [];
        this.form.sub_category_id = "";
        const res = await api.get(
          `/v1/categories/${this.form.category_id}/sub-categories`
        );
        this.subcategories = res.data;
      } catch (err) {
        console.error("Failed to load subcategories", err);
      }
    },
    async onSubcategoryChange() {
      try {
        const res = await api.get(
          `/v1/sub-categories/${this.form.sub_category_id}/fields`
        );
        this.dynamicFields = res.data.fields;
      } catch (err) {
        console.error("Failed to load dynamic fields", err);
      }
    },
    initMap() {
      this.map = L.map(this.$refs.mapContainer).setView([0, 0], 2);

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(this.map);

      this.map.on("click", (e) => {
        if (this.marker) {
          this.map.removeLayer(this.marker);
        }

        this.marker = L.marker([e.latlng.lat, e.latlng.lng]).addTo(this.map);
        this.form.latitude = e.latlng.lat ?? null;
        this.form.longitude = e.latlng.lng ?? null;
      });
    },
    async searchLocation() {
      if (!this.searchQuery) return;

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            this.searchQuery
          )}`
        );
        const results = await res.json();
        if (results.length > 0) {
          const lat = parseFloat(results[0].lat);
          const lon = parseFloat(results[0].lon);
          this.setMapLocation(lat, lon);
        } else {
          alert("Location not found.");
        }
      } catch (error) {
        console.error("Geocoding error:", error);
      }
    },
    useMyLocation() {
      if (!navigator.geolocation) {
        alert("Geolocation not supported by your browser");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          this.setMapLocation(lat, lon);
        },
        () => {
          alert("Unable to retrieve your location");
        }
      );
    },
    setMapLocation(lat, lon) {
      if (this.marker) this.map.removeLayer(this.marker);

      this.marker = L.marker([lat, lon]).addTo(this.map);

      // 🛠️ Force Leaflet to recalculate map size inside modal or flex
      this.$nextTick(() => {
        this.map.invalidateSize();

        this.map.setView([lat, lon], 13, {
          animate: true,
          duration: 0.5,
        });
      });

      this.form.latitude = lat ?? null;
      this.form.longitude = lon ?? null;
    },
    handleSubmit() {
      const hasImages =
        Array.isArray(this.form.images) && this.form.images.length > 0;

      if (!hasImages) {
        this.hasImageError = true;
        this.showImageError = true;
        this.$nextTick(() =>
          this.$refs.fileSection?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          })
        );
        return;
      }

      // Currency validation not needed - always using SAR

      if (!this.form.country_id || this.form.country_id === "") {
        alert(this.$t("products.addModal.pleaseSelectCountry"));
        return;
      }

      if (!this.form.state_id || this.form.state_id === "") {
        alert(this.$t("products.addModal.pleaseSelectState"));
        return;
      }

      if (!this.form.area_id || this.form.area_id === "") {
        alert(this.$t("products.addModal.pleaseSelectArea"));
        return;
      }

      const formData = new FormData();
      formData.append("title", this.form.title);
      formData.append("description", this.form.description || "");
      formData.append("price", this.form.price);
      
      // Always use SAR currency
      formData.append("currency_code", "SAR");
      console.log("Using hardcoded currency: SAR");
      
      formData.append("sub_category_id", this.form.sub_category_id);
      formData.append("country_id", this.form.country_id);
      formData.append("state_id", this.form.state_id);
      formData.append("area_id", this.form.area_id);
      formData.append("latitude", this.form.latitude ?? null);
      formData.append("longitude", this.form.longitude ?? null);

      // Debug logging
      console.log("=== FORM SUBMISSION DEBUG ===");
      console.log("currency: SAR (hardcoded)");
      console.log("country_id:", this.form.country_id);
      console.log("state_id:", this.form.state_id);
      console.log("area_id:", this.form.area_id);
      
      // Log all form data entries
      console.log("FormData entries being sent:");
      for (let [key, value] of formData.entries()) {
        console.log("  " + key + ":", value);
      }
      console.log("=== END DEBUG ===");

      for (const [key, value] of Object.entries(this.form.fields)) {
        if (Array.isArray(value)) {
          value.forEach((v, i) => formData.append(`fields[${key}][${i}]`, v));
        } else {
          formData.append(`fields[${key}]`, value);
        }
      }

      this.form.images.forEach((file, index) => {
        formData.append(`images[${index}]`, file);
      });

      this.$emit("submit", formData);
    },
  },
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Smooth transitions for interactive elements */
button,
input,
select,
.map-container {
  transition: all 0.2s ease;
}

/* Leaflet map fixes */
.leaflet-container {
  z-index: 0 !important;
}
</style>
